import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
} from "react";

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SCRIPT_URL =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileWidgetId = string;

type TurnstileApi = {
    render: (
        container: HTMLElement,
        options: {
            sitekey: string;
            theme?: TurnstileTheme;
            callback?: (token: string) => void;
            "expired-callback"?: () => void;
            "error-callback"?: () => void;
        }
    ) => TurnstileWidgetId;
    reset: (widgetId?: TurnstileWidgetId) => void;
    remove: (widgetId: TurnstileWidgetId) => void;
};

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

export type TurnstileTheme = "auto" | "light" | "dark";

export type TurnstileHandle = {
    reset: () => void;
};

export type TurnstileProps = {
    className?: string;
    sitekey: string;
    theme?: TurnstileTheme;
    onVerify?: (token: string) => void;
    onExpire?: () => void;
    onError?: () => void;
};

const loadTurnstileScript = () =>
    new Promise<void>((resolve, reject) => {
        if (window.turnstile) {
            resolve();
            return;
        }

        const existingScript = document.getElementById(
            TURNSTILE_SCRIPT_ID
        ) as HTMLScriptElement | null;

        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(), {
                once: true,
            });
            existingScript.addEventListener("error", () => reject(), {
                once: true,
            });
            return;
        }

        const script = document.createElement("script");
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.addEventListener("load", () => resolve(), { once: true });
        script.addEventListener("error", () => reject(), { once: true });
        document.head.appendChild(script);
    });

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(
    (
        { className, sitekey, theme = "light", onVerify, onExpire, onError },
        ref
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const widgetIdRef = useRef<TurnstileWidgetId>();
        const onVerifyRef = useRef<TurnstileProps["onVerify"]>(onVerify);
        const onExpireRef = useRef<TurnstileProps["onExpire"]>(onExpire);
        const onErrorRef = useRef<TurnstileProps["onError"]>(onError);

        useEffect(() => {
            onVerifyRef.current = onVerify;
            onExpireRef.current = onExpire;
            onErrorRef.current = onError;
        }, [onVerify, onExpire, onError]);

        useImperativeHandle(ref, () => ({
            reset: () => {
                if (widgetIdRef.current) {
                    window.turnstile?.reset(widgetIdRef.current);
                }
            },
        }));

        useEffect(() => {
            let cancelled = false;

            const renderWidget = async () => {
                try {
                    await loadTurnstileScript();
                } catch {
                    onErrorRef.current?.();
                    return;
                }

                if (cancelled || !containerRef.current || !window.turnstile) {
                    return;
                }

                widgetIdRef.current = window.turnstile.render(
                    containerRef.current,
                    {
                        sitekey,
                        theme: theme as TurnstileTheme,
                        callback: (token) => onVerifyRef.current?.(token),
                        "expired-callback": () => onExpireRef.current?.(),
                        "error-callback": () => onErrorRef.current?.(),
                    }
                );
            };

            void renderWidget();

            return () => {
                cancelled = true;
                if (widgetIdRef.current) {
                    window.turnstile?.remove(widgetIdRef.current);
                    widgetIdRef.current = undefined;
                }
            };
        }, [sitekey, theme]);

        return <div className={className} ref={containerRef} />;
    }
);

Turnstile.displayName = "Turnstile";
